import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { fetchAddonServicesThunk } from 'actions/addonServices/AddonServicesActions';
import {
    addPortalBookingAddons,
    fetchPortalBooking
} from 'actions/portalBooking/PortalBookingActions';
import { AddonsModeFooter } from 'components/AddonsMode/Footer/AddonsModeFooter';
import { AddonsSummary, SummaryPet } from 'components/AddonsMode/Summary/AddonsSummary';
import { Header } from 'components/Layout/Header/Header';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { Alert } from 'components/UI/Alert/Alert';
import { LoadingOverlay } from 'components/UI/LoadingOverlay/LoadingOverlay';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { useSelector } from 'react-redux';
import { getActiveAddonServices } from 'reducers/addonServices/AddonServicesSelectors';
import { getOnlineBookingState } from 'reducers/onlineBooking/OnlineBookingSelectors';
import { RootState, dispatch } from 'store';
import { AddonSchedule } from 'types/AddonSchedule';
import { AddonService } from 'types/AddonService';
import { PetType } from 'types/Pet';
import { PortalAddonsError, PortalAddonsErrorKind, PortalBooking } from 'types/PortalBooking';
import { createDefaultSchedule } from 'utils/addonSchedule';
import {
    AddonGroup,
    AddonSelectionLine,
    AddonSelections,
    buildAddonEntries,
    dropLockedDays,
    dropUnavailableEntries,
    getAddonGroups,
    getEligiblePets,
    getSelectionKey,
    getSelectionLines,
    getSelectionsTotal
} from 'utils/addonsMode';
import { goToPetParentPortal } from 'utils/petParentPortal';
import { v4 as uuidv4 } from 'uuid';

import { AddonsModeError } from './AddonsModeError';
import { AddonsModePetStep } from './AddonsModePetStep';
import { AddonsModeReviewStep } from './AddonsModeReviewStep';
import { AddonsModeSuccess } from './AddonsModeSuccess';
import classes from './AddonsMode.module.scss';

interface AddonsModeFlowProps {
    bookingId: number;
}

type FlowStep = { kind: 'pet'; index: number } | { kind: 'review' } | { kind: 'success' };

interface FlowNotice {
    variant: 'warning' | 'error';
    text: string;
}

const UNAVAILABLE_MESSAGE =
    'Some Add-Ons can no longer be added and were removed from your selection. Review it and continue.';
const LOCKED_MESSAGE =
    'Some days were added to this booking in the meantime and were removed from your selection. Review it and continue.';
const CONNECTION_MESSAGE = 'We couldn’t add the Add-Ons. Check your connection and try again.';
const REJECTED_MESSAGE =
    'We couldn’t add the Add-Ons to this booking. Review your selection and try again, or contact the business.';

// The flow's own progress: the pets fill the bar up to the review, the success completes it.
const getProgress = (step: FlowStep, petCount: number) => {
    if (step.kind === 'pet') {
        return 30 + (40 * (step.index + 1)) / Math.max(petCount, 1);
    }

    return step.kind === 'review' ? 85 : 100;
};

/**
 * Add Add-Ons to an existing booking, opened from the Pet Parent Portal: one step per pet, a
 * review with "Complete", then the success screen. New Add-Ons go to the booking balance.
 */
export const AddonsModeFlow: React.FC<AddonsModeFlowProps> = ({ bookingId }) => {
    const marketplace = useMarketplace();
    const catalog = useSelector(getActiveAddonServices);
    const { customer } = useSelector(getOnlineBookingState);
    const token = useSelector((state: RootState) => state.auth.token);

    const [booking, setBooking] = React.useState<PortalBooking | null>(null);
    const [loadFailed, setLoadFailed] = React.useState(false);
    const [step, setStep] = React.useState<FlowStep>({ kind: 'pet', index: 0 });
    const [completedPetIds, setCompletedPetIds] = React.useState<Array<number>>([]);
    const [selections, setSelections] = React.useState<AddonSelections>({});
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    // Two clicks in the same tick both see isSubmitting false: the ref stops the second POST.
    const submitting = React.useRef(false);
    const [notice, setNotice] = React.useState<FlowNotice | null>(null);
    const [showScheduleErrors, setShowScheduleErrors] = React.useState(false);
    const [errorScrollRequest, setErrorScrollRequest] = React.useState(0);

    // A line keeps its uuid while the page lives, so retrying a request whose answer never arrived
    // cannot add it twice (the backend answers a repeated uuid with the lines it saved).
    const uuids = React.useRef(new Map<string, string>());
    const uuidFor = React.useCallback((key: string) => {
        const uuid = uuids.current.get(key) ?? uuidv4();
        uuids.current.set(key, uuid);
        return uuid;
    }, []);

    React.useEffect(() => {
        if (!token) {
            return;
        }

        let active = true;

        fetchPortalBooking(bookingId, marketplace.id)
            .then(loaded => active && setBooking(loaded))
            .catch(() => active && setLoadFailed(true));

        return () => {
            active = false;
        };
    }, [bookingId, marketplace.id, token]);

    const petIds = React.useMemo(
        () => (booking ? getEligiblePets(booking).map(pet => pet.id) : []),
        [booking]
    );

    // Booking pets with what the signed-in customer's profile adds: breed and photo.
    const pets = React.useMemo<Array<SummaryPet>>(
        () =>
            booking
                ? getEligiblePets(booking).map(pet => {
                      const profile = customer?.pets.find(candidate => candidate.id === pet.id);

                      return {
                          id: pet.id,
                          name: pet.name,
                          type:
                              profile?.type?.id !== undefined
                                  ? profile.type
                                  : ({
                                        id: pet.petType,
                                        singular: pet.petType,
                                        plural: pet.petType
                                    } as PetType),
                          breed: profile?.breed?.name,
                          image: profile?.image?.src
                      };
                  })
                : [],
        [booking, customer]
    );

    const lines = React.useMemo(
        () => (booking ? getSelectionLines(booking, catalog, selections) : []),
        [booking, catalog, selections]
    );
    const total = getSelectionsTotal(lines);

    // A Select Days with no day blocks the flow until it gets one or is unselected: it is never
    // dropped on the way to the review or the POST.
    const emptySchedulePetIds = React.useMemo(
        () => petIds.filter(petId => lines.some(line => line.pet.id === petId && line.units === 0)),
        [petIds, lines]
    );

    React.useEffect(() => {
        if (emptySchedulePetIds.length === 0) {
            setShowScheduleErrors(false);
        }
    }, [emptySchedulePetIds.length]);

    React.useEffect(() => {
        if (errorScrollRequest > 0) {
            document
                .querySelector('[data-schedule-error]')
                ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
        }
    }, [errorScrollRequest]);

    const petIndex = step.kind === 'pet' ? Math.min(step.index, Math.max(pets.length - 1, 0)) : 0;
    const currentPetId = petIds[petIndex];

    const groups = React.useMemo(
        () =>
            booking && currentPetId !== undefined
                ? getAddonGroups(booking, currentPetId, catalog)
                : [],
        [booking, catalog, currentPetId]
    );

    const goToPortal = () => goToPetParentPortal(marketplace.slug, bookingId);

    // Stops on the pet with an empty Select Days (the current one first) and marks it as an error.
    const stopOnEmptySchedule = () => {
        if (emptySchedulePetIds.length === 0) {
            return false;
        }

        const petId =
            step.kind === 'pet' && emptySchedulePetIds.includes(currentPetId)
                ? currentPetId
                : emptySchedulePetIds[0];

        setShowScheduleErrors(true);
        setStep({ kind: 'pet', index: petIds.indexOf(petId) });
        setErrorScrollRequest(current => current + 1);
        return true;
    };

    const changeSelection = (
        group: AddonGroup,
        addon: AddonService,
        schedule: AddonSchedule | null
    ) => {
        const key = getSelectionKey(group.key, addon.id);

        setSelections(current => {
            const petSelections = { ...current[currentPetId] };

            if (schedule) {
                petSelections[key] = schedule;
            } else {
                delete petSelections[key];
            }

            return { ...current, [currentPetId]: petSelections };
        });
    };

    const removeLine = (line: AddonSelectionLine) =>
        setSelections(current => {
            const petSelections = { ...current[line.pet.id] };
            delete petSelections[line.selectionKey];
            return { ...current, [line.pet.id]: petSelections };
        });

    const refreshBooking = async () => {
        const [refreshed] = await Promise.all([
            fetchPortalBooking(bookingId, marketplace.id).catch(() => null),
            dispatch(fetchAddonServicesThunk(marketplace.id)).catch(() => undefined)
        ]);

        if (refreshed) {
            setBooking(refreshed);
        }

        return refreshed;
    };

    const handleSubmitError = async (
        error: PortalAddonsError,
        entries: ReturnType<typeof buildAddonEntries>
    ) => {
        if (error.kind === PortalAddonsErrorKind.NotAvailable) {
            setBooking(current => (current ? { ...current, canAddAddons: false } : current));
            return;
        }

        if (error.kind === PortalAddonsErrorKind.PriceChanged) {
            await dispatch(fetchAddonServicesThunk(marketplace.id)).catch(() => undefined);
            setNotice({
                variant: 'warning',
                text: 'Some prices changed. Review the new total and complete again.'
            });
            return;
        }

        const refreshed = await refreshBooking();
        let next = selections;
        let text =
            error.kind === PortalAddonsErrorKind.Rejected ? REJECTED_MESSAGE : CONNECTION_MESSAGE;

        if (error.kind === PortalAddonsErrorKind.AddonsUnavailable) {
            const rejected = (error.positions ?? [])
                .map(position => entries[position])
                .filter(Boolean);
            // Without positions there is no telling which ones: start over.
            next = rejected.length > 0 ? dropUnavailableEntries(selections, lines, rejected) : {};
            text = UNAVAILABLE_MESSAGE;
        }

        if (refreshed) {
            const reconciled = dropLockedDays(refreshed, next);
            next = reconciled.selections;

            if (reconciled.changed && error.kind !== PortalAddonsErrorKind.AddonsUnavailable) {
                text = LOCKED_MESSAGE;
            }
        }

        setSelections(next);
        setNotice({
            variant:
                error.kind === PortalAddonsErrorKind.Unknown ||
                error.kind === PortalAddonsErrorKind.Rejected
                    ? 'error'
                    : 'warning',
            text
        });
    };

    const submitHandler = async () => {
        if (!booking || lines.length === 0 || submitting.current || stopOnEmptySchedule()) {
            return;
        }

        const entries = buildAddonEntries(lines, uuidFor);

        submitting.current = true;
        setIsSubmitting(true);
        setNotice(null);

        try {
            await addPortalBookingAddons(booking.id, marketplace.id, entries, total);
            setStep({ kind: 'success' });
        } catch (error) {
            await handleSubmitError(error as PortalAddonsError, entries);
        } finally {
            submitting.current = false;
            setIsSubmitting(false);
        }
    };

    if (!token) {
        return (
            <AddonsModeError
                bookingId={bookingId}
                title="Your session expired"
                message="Open Add Add-Ons again from your booking in the Pet Parent Portal."
            />
        );
    }

    if (step.kind === 'success') {
        return <AddonsModeSuccess bookingId={bookingId} />;
    }

    if (loadFailed) {
        return (
            <AddonsModeError
                bookingId={bookingId}
                message="This booking isn’t available right now. Go back to the Pet Parent Portal and try again."
            />
        );
    }

    if (!booking) {
        return <LoadingOverlay />;
    }

    if (!booking.canAddAddons || pets.length === 0) {
        return (
            <AddonsModeError
                bookingId={bookingId}
                title="Add-Ons can’t be added"
                message="This booking can’t receive more Add-Ons. Go back to the Pet Parent Portal to see its details."
            />
        );
    }

    const noticeAlert = notice && (
        <Alert variant={notice.variant} icon={faCircleExclamation} className={classes.alert}>
            {notice.text}
        </Alert>
    );

    const summary = (onRemove?: (line: AddonSelectionLine) => void, hideTitle?: boolean) => (
        <AddonsSummary
            pets={pets}
            lines={lines}
            saved={booking.addons}
            hideTitle={hideTitle}
            onRemove={onRemove}
        />
    );

    const isReview = step.kind === 'review';
    const isLastPet = petIndex === pets.length - 1;
    const currentPetLines = lines.filter(line => line.pet.id === currentPetId);

    const nextHandler = () => {
        setNotice(null);

        if (stopOnEmptySchedule()) {
            return;
        }

        setCompletedPetIds(current => Array.from(new Set([...current, currentPetId])));
        setStep(isLastPet ? { kind: 'review' } : { kind: 'pet', index: petIndex + 1 });
    };

    const petLabel = isLastPet ? 'Continue' : currentPetLines.length === 0 ? 'Skip' : 'Next Pet';

    return (
        <TwoColumnsLayout
            header={
                <Header
                    logo={marketplace.logo}
                    progress={getProgress(step, pets.length)}
                    backLabel={isReview ? 'Back' : 'Exit'}
                    onBack={
                        isReview
                            ? () => setStep({ kind: 'pet', index: pets.length - 1 })
                            : goToPortal
                    }
                />
            }
        >
            <LeftColumn>
                {isReview ? (
                    <AddonsModeReviewStep
                        lines={lines}
                        notice={noticeAlert}
                        mobileSummary={summary(undefined, true)}
                        onEdit={() => setStep({ kind: 'pet', index: 0 })}
                        onClear={() => setSelections({})}
                    />
                ) : (
                    <AddonsModePetStep
                        booking={booking}
                        pets={pets}
                        petId={currentPetId}
                        completedPetIds={completedPetIds.filter(
                            petId => !emptySchedulePetIds.includes(petId)
                        )}
                        groups={groups}
                        selections={selections}
                        showScheduleErrors={showScheduleErrors}
                        notice={noticeAlert}
                        onSelectPet={petId =>
                            setStep({ kind: 'pet', index: petIds.indexOf(petId) })
                        }
                        onToggle={(group, addon, checked) =>
                            changeSelection(group, addon, checked ? createDefaultSchedule() : null)
                        }
                        onScheduleChange={changeSelection}
                    />
                )}
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <AddonsModeFooter
                        total={total}
                        label={isReview ? 'Complete' : petLabel}
                        loading={isSubmitting}
                        disabled={isReview && (lines.length === 0 || isSubmitting)}
                        mobileSummary={isReview ? undefined : summary(removeLine, true)}
                        onSubmit={isReview ? submitHandler : nextHandler}
                    />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    {summary(isReview ? undefined : removeLine)}
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
};
